import { useState, useEffect, useCallback } from 'react';
import { contractsService, contractsUtils } from 'api/services/contracts';
import { CONTRACT_STATUS, PAGE_CONFIG } from '../constants';

const { ALL, ACTIVE, IN_PROGRESS, COMPLETED, CANCELLED } = CONTRACT_STATUS;
const { PAGE_SIZE, PAGE_NO } = PAGE_CONFIG;

const useContract = (filters) => {
  const [contracts, setContracts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStatCard, setSelectedStatCard] = useState(null);
  const [pagination, setPagination] = useState({
    page: PAGE_NO,
    pageSize: PAGE_SIZE,
  });
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(PAGE_NO);

  // Format contract data - handle Go nullable types
  const formatContractData = useCallback((contract) => {
    // Helper function to extract value from nullable database fields
    const extractValue = (field) => {
      if (field === null || field === undefined) return null;
      if (typeof field === 'object' && field.hasOwnProperty('Int32')) {
        return field.Valid ? field.Int32 : null;
      }
      if (typeof field === 'object' && field.hasOwnProperty('Float64')) {
        return field.Valid ? field.Float64 : null;
      }
      if (typeof field === 'object' && field.hasOwnProperty('String')) {
        return field.Valid ? field.String : null;
      }
      if (typeof field === 'object' && field.hasOwnProperty('Time')) {
        return field.Valid ? new Date(field.Time) : null;
      }
      return field;
    };

    // Format milestones to extract nullable values
    const formatMilestones = (milestones) => {
      if (!milestones || !Array.isArray(milestones)) return [];
      return milestones.map((milestone) => ({
        ...milestone,
        amount_usd: extractValue(milestone.amount_usd) || 0,
        description: extractValue(milestone.description) || '',
        due_date: extractValue(milestone.due_date),
        release_condition: extractValue(milestone.release_condition) || '',
        completed_at: extractValue(milestone.completed_at),
        approved_at: extractValue(milestone.approved_at),
      }));
    };

    return {
      ...contract,
      techStack: contract.tech_stack || [],
      createdAt: new Date(contract.created_at),
      updatedAt: new Date(contract.updated_at),
      startDate: extractValue(contract.start_date),
      endDate: extractValue(contract.end_date),
      // Extract nullable field values
      expected_duration_days: extractValue(contract.expected_duration_days),
      hourly_rate: extractValue(contract.hourly_rate),
      amount_usd: extractValue(contract.amount_usd) || 0,
      submission_count: extractValue(contract.submission_count) || 0,
      view_count: extractValue(contract.view_count) || 0,
      // Format nested data
      milestones: formatMilestones(contract.milestones),
      // Extract business name safely
      business_name: contract.business?.name || 'Unknown Business',
      // Extract worker info safely
      worker_name: contract.worker?.name || null,
    };
  }, []);

  // Fetch contracts
  const fetchContracts = useCallback(
    async (page = PAGE_NO, append = false, filtersToUse = filters) => {
      try {
        if (append) {
          setIsLoadingMore(true);
        } else {
          setIsLoading(true);
        }

        const response = await contractsService.getContracts({
          page,
          limit: PAGE_SIZE,
          ...filtersToUse,
          techStack: filtersToUse?.techStack?.join(','),
        });

        if (response.success && response.data) {
          const contractsData = response.data.data || response.data;
          const contractsArray = Array.isArray(contractsData)
            ? contractsData
            : [];
          const formattedContracts = contractsArray.map(formatContractData);

          if (append) {
            setContracts((prev) => [...prev, ...formattedContracts]);
          } else {
            setContracts(formattedContracts);
          }

          setHasMore(formattedContracts.length === PAGE_SIZE);
          setCurrentPage(page);
        } else {
          throw new Error(response.message || 'Failed to fetch contracts');
        }
      } catch (err) {
        console.error('Error fetching contracts:', err);
        setError(err.message || 'Failed to load contracts');
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [filters, formatContractData]
  );

  // Calculate statistics
  const calculateStats = useCallback(() => {
    const totalContracts = contracts.length;
    const activeContracts = contracts.filter((c) => c.status === ACTIVE).length;
    const completedContracts = contracts.filter(
      (c) => c.status === COMPLETED
    ).length;
    const upcomingContracts = contracts.filter(
      (c) => c.status === IN_PROGRESS
    ).length;
    const cancelledContracts = contracts.filter(
      (c) => c.status === CANCELLED
    ).length;

    return {
      totalContracts,
      activeContracts,
      completedContracts,
      upcomingContracts,
      cancelledContracts,
    };
  }, [contracts]);

  // Handle stat card click for filtering
  const handleStatCardClick = useCallback(
    (cardType) => {
      if (selectedStatCard === cardType) {
        setSelectedStatCard(null);
      } else {
        setSelectedStatCard(cardType);
      }
    },
    [selectedStatCard]
  );

  // Search text filter across allowed keys
  const searchFilterHandler = useCallback((contract, searchTextRaw) => {
    if (!searchTextRaw) return true;
    const searchText = String(searchTextRaw).trim().toLowerCase();
    if (!searchText) return true;

    const title = (contract?.title || '').toLowerCase();
    const businessName = (contract?.business_name || '').toLowerCase();
    const techArr = Array.isArray(contract?.techStack)
      ? contract.techStack
      : [];
    const techHit = techArr.some((t) =>
      String(t || '')
        .toLowerCase()
        .includes(searchText)
    );

    const amount = Number(contract?.amount_usd ?? 0);
    const amountStrRaw = isNaN(amount) ? '' : String(amount);
    const amountFormatted = contractsUtils
      .formatCurrency(isNaN(amount) ? 0 : amount)
      .replace(/[$,\s]/g, '')
      .toLowerCase();
    const amountHit =
      amountStrRaw.toLowerCase().includes(searchText) ||
      amountFormatted.includes(searchText);

    return (
      title.includes(searchText) ||
      businessName.includes(searchText) ||
      techHit ||
      amountHit
    );
  }, []);

  // Filter contracts based on selected stat card and search text
  const getFilteredContracts = useCallback(() => {
    let filtered = contracts;

    // Apply stat card filter first
    if (selectedStatCard) {
      switch (selectedStatCard) {
        case ALL:
          filtered = contracts;
          break;
        case ACTIVE:
          filtered = contracts.filter((contract) => contract.status === ACTIVE);
          break;
        case COMPLETED:
          filtered = contracts.filter(
            (contract) => contract.status === COMPLETED
          );
          break;
        case IN_PROGRESS:
          filtered = contracts.filter(
            (contract) => contract.status === IN_PROGRESS
          );
          break;
        case CANCELLED:
          filtered = contracts.filter(
            (contract) => contract.status === CANCELLED
          );
          break;
        default:
          filtered = contracts;
      }
    }

    // Apply search text filter (client-side refinement)
    const searchText = filters?.search;
    if (searchText && String(searchText).trim()) {
      filtered = filtered.filter((c) => searchFilterHandler(c, searchText));
    }

    return filtered;
  }, [contracts, selectedStatCard, filters, searchFilterHandler]);

  // Load more contracts
  const loadMore = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      fetchContracts(currentPage + 1, true, filters);
    }
  }, [isLoadingMore, hasMore, currentPage, fetchContracts, filters]);

  // Refresh contracts
  const refreshContracts = useCallback(
    (refreshFilters) => {
      setCurrentPage(PAGE_NO);
      fetchContracts(PAGE_NO, false, refreshFilters);
    },
    [fetchContracts]
  );

  return {
    // State
    contracts,
    isLoading,
    isLoadingMore,
    error,
    selectedStatCard,
    hasMore,
    currentPage,

    // Computed values
    filteredContracts: getFilteredContracts(),
    stats: calculateStats(),

    // Actions
    fetchContracts,
    loadMore,
    refreshContracts,
    handleStatCardClick,
    setSelectedStatCard,
    setError,
    searchFilterHandler,
  };
};

export default useContract;
